import { useSyncExternalStore } from 'react'
import { OmneoList, OmneoListItem } from '../types'
import { wishlistProviderService } from './provider.service'
import { normalizeWishlistComparableProperties } from './utils'

type WishlistVariantMembership = {
  list: OmneoList
  item: OmneoListItem
}

type WishlistStoreState = {
  lists: OmneoList[]
  isInitialized: boolean
  isLoading: boolean
  isLoggedIn: boolean
  isReady: boolean
}

export type WishlistStoreSnapshot = WishlistStoreState & {
  hasAnyItems: boolean
  listByHandle: Map<string, OmneoList>
  variantMembershipByKey: Map<string, WishlistVariantMembership>
  variantMembershipById: Map<number, WishlistVariantMembership>
}

const listeners = new Set<() => void>()

let isStoreInitialized = false
let storeState: WishlistStoreState = {
  lists: [],
  isInitialized: false,
  isLoading: false,
  isLoggedIn: false,
  isReady: false
}

let storeSnapshot = buildSnapshot(storeState)

function buildVariantMembershipKey(variantId: unknown, properties: unknown) {
  const parsedVariantId = typeof variantId === 'number' ? variantId : Number(variantId)

  if (!Number.isFinite(parsedVariantId) || parsedVariantId <= 0) {
    return ''
  }

  return `${parsedVariantId}:${JSON.stringify(normalizeWishlistComparableProperties(properties))}`
}

function buildSnapshot(state: WishlistStoreState): WishlistStoreSnapshot {
  const listByHandle = new Map<string, OmneoList>()
  const variantMembershipByKey = new Map<string, WishlistVariantMembership>()
  const variantMembershipById = new Map<number, WishlistVariantMembership>()

  state.lists.forEach((list) => {
    list.items.forEach((item) => {
      const handle = item?.meta?.handle
      if (typeof handle === 'string' && handle && !listByHandle.has(handle)) {
        listByHandle.set(handle, list)
      }

      const variantKey = buildVariantMembershipKey(item?.meta?.variantId, item?.meta?.properties)
      if (variantKey && !variantMembershipByKey.has(variantKey)) {
        variantMembershipByKey.set(variantKey, { list, item })
      }

      const variantId = Number(item?.meta?.variantId)
      if (Number.isFinite(variantId) && variantId > 0 && !variantMembershipById.has(variantId)) {
        variantMembershipById.set(variantId, { list, item })
      }
    })
  })

  return {
    ...state,
    hasAnyItems: state.lists.some((list) => list.items.length > 0),
    listByHandle,
    variantMembershipByKey,
    variantMembershipById
  }
}

function emitChange() {
  listeners.forEach((listener) => {
    listener()
  })
}

function setStoreState(nextState: WishlistStoreState) {
  storeState = nextState
  storeSnapshot = buildSnapshot(nextState)
  emitChange()
}

function updateStoreState(patch: Partial<WishlistStoreState>) {
  setStoreState({
    ...storeState,
    ...patch
  })
}

function syncStoreFromCache() {
  const isLoggedIn = wishlistProviderService.isCustomerLoggedIn()
  const isReady = wishlistProviderService.isReady()

  if (!isLoggedIn) {
    wishlistProviderService.clearCachedLists()
    setStoreState({
      lists: [],
      isInitialized: true,
      isLoading: false,
      isLoggedIn: false,
      isReady
    })
    return
  }

  const cachedLists = wishlistProviderService.getCachedLists()

  setStoreState({
    lists: cachedLists,
    isInitialized: true,
    isLoading: isReady && cachedLists.length === 0,
    isLoggedIn: true,
    isReady
  })
}

function initializeWishlistStore() {
  if (isStoreInitialized || typeof window === 'undefined') {
    return
  }

  isStoreInitialized = true
  syncStoreFromCache()

  wishlistProviderService.onReady(() => {
    updateStoreState({
      isReady: true,
      isInitialized: true
    })

    refreshWishlistStore().catch((error) => {
      console.warn('Could not refresh wishlist store after provider became ready', error)
    })
  })

  wishlistProviderService.subscribeToListChanges(() => {
    refreshWishlistStore().catch((error) => {
      console.warn('Could not refresh wishlist store after wishlist change', error)
    })
  })
}

function subscribe(listener: () => void) {
  initializeWishlistStore()
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  initializeWishlistStore()
  return storeSnapshot
}

export async function refreshWishlistStore({ syncWithStorefront = false } = {}) {
  const isLoggedIn = wishlistProviderService.isCustomerLoggedIn()
  const isReady = wishlistProviderService.isReady()

  if (!isLoggedIn) {
    wishlistProviderService.clearCachedLists()
    setStoreState({
      lists: [],
      isInitialized: true,
      isLoading: false,
      isLoggedIn: false,
      isReady
    })
    return []
  }

  if (!isReady) {
    updateStoreState({
      isInitialized: true,
      isLoading: false,
      isLoggedIn: true,
      isReady: false
    })
    return storeState.lists
  }

  updateStoreState({
    isInitialized: true,
    isLoading: isReady && storeState.lists.length === 0,
    isLoggedIn: true,
    isReady: true
  })

  try {
    const lists = await wishlistProviderService.getLists({ syncWithStorefront })

    setStoreState({
      lists,
      isInitialized: true,
      isLoading: false,
      isLoggedIn: true,
      isReady: true
    })

    return lists
  } catch (error) {
    updateStoreState({
      isInitialized: true,
      isLoading: false,
      isLoggedIn: true,
      isReady
    })

    throw error
  }
}

export function useWishlistStore() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function useWishlistListForHandle(handle: string | null | undefined) {
  const store = useWishlistStore()

  if (!handle) {
    return null
  }

  return store.listByHandle.get(handle) || null
}

export function useWishlistVariantMembership(variantId: unknown, properties: unknown, matchVariantOnly = false) {
  const store = useWishlistStore()
  const variantKey = buildVariantMembershipKey(variantId, properties)

  if (!variantKey) {
    return null
  }

  const membership = store.variantMembershipByKey.get(variantKey)

  if (membership || !matchVariantOnly) {
    return membership || null
  }

  return store.variantMembershipById.get(Number(variantId)) || null
}